import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { CheckCircle2, CircleDollarSign, AlertCircle } from 'lucide-react';

const PALETTE = ['#2563eb', '#ea580c', '#16a34a', '#0d9488', '#db2777', '#7c3aed', '#0891b2', '#ca8a04', '#4f46e5', '#dc2626'];
const colorFor = (s) => PALETTE[[...String(s)].reduce((a, c) => a + c.charCodeAt(0), 0) % PALETTE.length];
const DOT = { led: '#3730a3', deroulant: '#f59e0b', grand: '#16a34a', poteau: '#8b5cf6' };
const LABEL_COLOR = { led: 'text-indigo-800', deroulant: 'text-amber-600', grand: 'text-green-600', poteau: 'text-violet-600' };
const J = ['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa'];
export const fmtF = (n) => Math.round(Number(n) || 0).toLocaleString('fr-FR').replace(/[\u202f\u00a0]/g, ' ');
const fmtShort = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
const dayIdx = (iso, start) => Math.round((new Date(`${iso}T00:00:00`) - new Date(`${start}T00:00:00`)) / 86400000);

const PayIcon = ({ p }) => (p === 'paye' ? <CheckCircle2 className="h-4 w-4 text-white/90" /> : p === 'partiel' ? <CircleDollarSign className="h-4 w-4 text-amber-200" /> : <AlertCircle className="h-4 w-4 text-rose-200" />);

function BookingBar({ b, face, data, onRenew, onView }) {
  const s = Math.max(0, dayIdx(b.debut, data.start));
  const e = Math.min(data.days - 1, dayIdx(b.fin, data.start) - 1);
  if (e < 0 || s > data.days - 1) return null;
  const left = (s / data.days) * 100; const width = ((e - s + 1) / data.days) * 100;
  const renew = b.expire_dans >= 0 && b.expire_dans <= 14;
  const c = colorFor(b.client);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className={`absolute top-1 bottom-1 rounded-md px-2 text-left text-white text-xs font-bold flex items-center justify-between gap-1 overflow-hidden transition-[filter,transform] hover:brightness-110 hover:-translate-y-px ${renew ? 'ring-2 ring-amber-400 ring-offset-1' : ''}`}
          style={{ left: `${left}%`, width: `calc(${width}% - 3px)`, background: c }} data-testid={`booking-${face.code}-${b.debut}`}>
          <span className="truncate">{b.client}</span><PayIcon p={b.paiement} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 rounded-2xl" data-testid="booking-popover">
        <div className="flex items-center gap-2 text-slate-600 font-semibold"><span className="w-3 h-3 rounded-full" style={{ background: DOT[face.type] }} />{face.code} · {face.emplacement}</div>
        <div className="inline-block mt-3 px-3 py-1 rounded-lg text-white font-bold" style={{ background: c }}>{b.client}</div>
        <div className="grid grid-cols-[70px_1fr] gap-y-1.5 mt-3 text-sm">
          <span className="text-slate-500">Période</span><b>{fmtShort(b.debut)} → {fmtShort(b.fin)} ({dayIdx(b.fin, b.debut)} j)</b>
          <span className="text-slate-500">Affiches</span><b>{b.affiches}</b>
          <span className="text-slate-500">Tarif</span><b>{fmtF(b.tarif_jour)} FCFA / jour</b>
          <span className="text-slate-500">Total</span><b>{fmtF(b.total)} FCFA</b>
        </div>
        <div className={`inline-block mt-3 text-xs font-bold px-2 py-1 rounded-md ${b.paiement === 'paye' ? 'bg-emerald-50 text-emerald-700' : b.paiement === 'partiel' ? 'bg-amber-50 text-amber-700' : 'bg-rose-50 text-rose-700'}`}>
          {b.paiement === 'paye' ? 'Payé' : b.paiement === 'partiel' ? `Partiel · ${b.pct_paye} % payé` : 'Non payé'}
        </div>
        <div className="grid grid-cols-2 gap-2 mt-3">
          <button onClick={() => onRenew(b)} className="h-9 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold transition-colors" data-testid="popover-renew">Renouveler</button>
          <button onClick={() => onView(b)} className="h-9 rounded-full border-2 border-blue-600 text-blue-700 text-sm font-bold hover:bg-blue-50 transition-colors" data-testid="popover-view">Voir le contrat</button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function freeGaps(face, data) {
  const busy = new Array(data.days).fill(false);
  face.bookings.forEach((b) => {
    for (let i = Math.max(0, dayIdx(b.debut, data.start)); i <= Math.min(data.days - 1, dayIdx(b.fin, data.start) - 1); i += 1) busy[i] = true;
  });
  const gaps = []; let i = 0;
  while (i < data.days) {
    if (!busy[i]) { const s = i; while (i < data.days && !busy[i]) i += 1; gaps.push([s, i - 1]); } else i += 1;
  }
  return gaps;
}

export function OccupationCalendar({ data, onFree, onRenew, onView }) {
  const days = Array.from({ length: data.days }, (_, i) => { const d = new Date(`${data.start}T00:00:00`); d.setDate(d.getDate() + i); return d; });
  const todayI = dayIdx(data.today, data.start);
  const grid = { gridTemplateColumns: '150px 1fr' };
  return (
    <div className="overflow-x-auto" data-testid="occupation-calendar">
      <div className="min-w-[900px]">
        <div className="grid" style={grid}>
          <div />
          <div className="relative flex">
            {days.map((d, i) => (
              <div key={i} className={`flex-1 text-center leading-tight ${i === todayI ? 'text-rose-600 font-bold' : 'text-slate-500'}`}>
                <div className="text-[10px]">{J[d.getDay()]}</div><div className="text-xs font-semibold">{d.getDate()}</div>
              </div>
            ))}
          </div>
        </div>
        {data.groups.map((g) => (
          <div key={g.ligne}>
            <div className="flex items-center justify-between bg-slate-50 rounded-md mt-2 px-2 py-1">
              <span className={`text-xs font-extrabold uppercase ${LABEL_COLOR[g.type]}`}>{g.label} <span className="font-normal normal-case text-slate-500">· {g.faces} face{g.faces > 1 ? 's' : ''} · {fmtF(g.tarif_jour)} FCFA/j</span></span>
              <span className="text-xs font-bold text-slate-700" data-testid={`group-occ-${g.type}`}>{g.occupe_pct} % occupé</span>
            </div>
            {data.faces.filter((f) => f.ligne === g.ligne).map((f) => (
              <div key={f.id} className="grid items-center h-8" style={grid} data-testid={`face-row-${f.code}`}>
                <div className="flex items-center gap-2 text-sm font-bold text-slate-900 pl-2 min-w-0" title={f.emplacement}>
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: DOT[f.type] }} />{f.code}
                </div>
                <div className="relative h-full">
                  {freeGaps(f, data).map(([s, e]) => (
                    <button key={s} onClick={() => onFree(f, s, e)} title="Cliquez pour réserver"
                      className="absolute top-1 bottom-1 rounded-md border border-slate-200 group transition-colors hover:border-blue-400"
                      style={{ left: `${(s / data.days) * 100}%`, width: `calc(${((e - s + 1) / data.days) * 100}% - 3px)`, background: 'repeating-linear-gradient(135deg,#f1f5f9 0 4px,#fff 4px 8px)' }}
                      data-testid={`free-${f.code}-${s}`}>
                      {e - s >= 3 && <span className="absolute inset-0 m-auto h-5 w-fit px-2 rounded-full bg-white border border-slate-200 text-[10px] font-bold text-blue-700 flex items-center group-hover:border-blue-400">+ Libre · {e - s + 1} j</span>}
                    </button>
                  ))}
                  {f.bookings.map((b, i) => <BookingBar key={i} b={b} face={f} data={data} onRenew={onRenew} onView={onView} />)}
                  {todayI >= 0 && todayI < data.days && <div className="absolute top-0 bottom-0 w-0.5 bg-rose-500/80 pointer-events-none" style={{ left: `${((todayI + 0.5) / data.days) * 100}%` }} />}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
